import React, { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Button, ButtonIcon, ConditionalLink, Flex, Hr, Loading, RichContent, Typography } from 'components';
import { contentsTypes, useContents } from 'features/contents';
import { createUrl, request, stripTags } from 'app/utils';
import { useSchool } from 'features/school';
import { ProductTypeEnum } from 'features/products';

const ContentArticleView = (): ReactElement => {
  const { contentDetails, clearContentDetails } = useContents();
  const { currentTheme } = useTheme();
  const { productSlug } = useParams();
  const { apiGatewayUrl } = useSchool();

  useEffect(() => {
    if (contentDetails && contentDetails.contentUuid) {
      request({
        url: `/api/v1/School/AssessContentArticleRead`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          contentUuid: contentDetails.contentUuid,
          productClassUuid: contentDetails.productClassUuid,
        },
      });
    }
  }, [contentDetails]);

  useEffect(() => {
    return () => {
      clearContentDetails();
    };
  }, []);

  if (!(contentDetails && contentDetails.article)) return <Loading label="Por favor, aguarde." />;

  if (contentDetails.article.articleText.search('data-tf-widget') !== -1) {
    document.body.appendChild(document.createElement('script')).src = '//embed.typeform.com/next/embed.js';
  }

  return (
    <Flex p={{ m: 30, d: 60 }} bg="articleBg">
      <Flex maxWidth={{ d: '65%' }} flexDirection="column">
        <Typography type="type3" color="color80" upper>
          {contentDetails.timelineOrder} / {contentDetails.timelineCount}
          {' • '}
          {contentDetails.productClassTimelineName || contentDetails.productName}
        </Typography>

        <Typography type="type6" fontWeight="bold" mt={{ m: 1, d: 2 }} color="color100">
          {contentDetails.name}
        </Typography>

        <Hr my={{ m: 3, d: 5 }} color="articleLine" />

        <RichContent defaultType="type3" currentTheme={currentTheme}>
          {contentDetails.article && contentDetails.article.articleText}
        </RichContent>

        <Hr my={{ m: 3, d: 5 }} color="articleLine" />

        <Flex justifyContent="center">
          <Flex flexGrow={0}>
            {Boolean(contentDetails && contentDetails.previousContentUuid) && (
              <ConditionalLink
                to={createUrl(
                  contentDetails.productType === ProductTypeEnum.Course
                    ? contentDetails.previousProductClassTimelineUuid
                      ? 'contentClassDetails'
                      : 'productContentClassDetails'
                    : 'productNoClassContent',
                  [
                    productSlug,
                    contentDetails.productClassUuid,
                    contentDetails.previousProductClassTimelineUuid,
                    contentDetails.previousContentUuid,
                    contentsTypes(contentDetails.previousContentType, contentDetails.previousExternalDocumentType),
                  ]
                )}
                type="internal"
                replace
              >
                <ButtonIcon
                  width={40}
                  height={40}
                  borderRadius={20}
                  iconWidth={25}
                  icon="chevronLeft"
                  title={`${stripTags(contentDetails.previousProductClassTimelineName)} - ${stripTags(contentDetails.previousContentName)}`}
                  style={{ whiteSpace: 'pre' }}
                  mr={2}
                />
              </ConditionalLink>
            )}

            {Boolean(contentDetails && contentDetails.nextContentUuid) && (
              <ConditionalLink
                to={createUrl(
                  contentDetails.productType === ProductTypeEnum.Course
                    ? contentDetails.nextProductClassTimelineUuid
                      ? 'contentClassDetails'
                      : 'productContentClassDetails'
                    : 'productNoClassContent',
                  [
                    productSlug,
                    contentDetails.productClassUuid,
                    contentDetails.nextProductClassTimelineUuid,
                    contentDetails.nextContentUuid,
                    contentsTypes(contentDetails.nextContentType, contentDetails.nextExternalDocumentType),
                  ]
                )}
                type="internal"
                replace
              >
                <Button>Próxima aula</Button>
              </ConditionalLink>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default ContentArticleView;
